// frontend/src/components/OpeningBalanceList.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Edit,
  Trash2,
  Plus,
  Building,
  User,
  Package,
  Warehouse,
  Download,
  Upload,
  Filter,
  Search,
  Hash,
  DollarSign,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./OpeningBalanceList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const OpeningBalanceList = () => {
  const { company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter that uses company's currency
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  const [openingBalances, setOpeningBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [userCompany, setUserCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [filters, setFilters] = useState({
    product_id: "",
    warehouse_id: "",
    search: "",
    status: "active"
  });
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Computed stats from the actual data (no backend dependency)
  const totalRecords = openingBalances.length;
  const totalQuantity = filteredData.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalValue = filteredData.reduce((sum, item) => sum + (item.total_cost || 0), 0);

  const fetchOpeningBalances = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await axios.get(`${url}/api/openingbalance`, {
        headers: getAuthHeaders(),
        params: {
          status: filters.status,
          product_id: filters.product_id || undefined,
          warehouse_id: filters.warehouse_id || undefined,
          search: filters.search || undefined
        }
      });

      if (!res.data.success) {
        throw new Error(res.data.message || "API returned unsuccessful response");
      }

      const data = res.data.data || [];

      setOpeningBalances(data);
      setFilteredData(data);

      // Set user company
      if (data[0]?.company_id) {
        setUserCompany(data[0].company_id);
      } else if (company) {
        setUserCompany(company);
      }

    } catch (error) {
      let errorMessage = "Failed to load opening balances";
      let detailedError = "";

      if (error.response) {
        errorMessage = `Server error (${error.response.status})`;
        detailedError = error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText;
      } else if (error.request) {
        errorMessage = "No response from server";
        detailedError = "Please check if the backend server is running and accessible.";
      } else if (error.message.includes("Network Error")) {
        errorMessage = "Network error";
        detailedError = "Cannot connect to server. Check your internet connection.";
      } else {
        errorMessage = error.message;
      }

      setError(`${errorMessage}${detailedError ? `\nDetails: ${detailedError}` : ''}`);
      toast.error(errorMessage);

      setOpeningBalances([]);
      setFilteredData([]);

    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      // Fetch products
      const productsRes = await axios.get(`${url}/api/products/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000
      });

      if (productsRes.data.success) {
        setProducts(productsRes.data.data || []);
      }

      // Fetch warehouses
      const warehousesRes = await axios.get(`${url}/api/warehouse/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000
      });

      if (warehousesRes.data.success) {
        setWarehouses(warehousesRes.data.data || []);
      }

    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    fetchOpeningBalances();
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, openingBalances]);

  const filterData = () => {
    let filtered = [...openingBalances];

    if (filters.product_id) {
      filtered = filtered.filter(item => {
        const productId = item.product_id?._id || item.product_id;
        return productId === filters.product_id;
      });
    }

    if (filters.warehouse_id) {
      filtered = filtered.filter(item => {
        const warehouseId = item.warehouse_id?._id || item.warehouse_id;
        return warehouseId === filters.warehouse_id;
      });
    }

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => {
        const productName = item.product_id?.product_name || '';
        const productCode = item.product_id?.product_code || item.product_id?.product_no || '';
        const warehouseName = item.warehouse_id?.warehouse_name || '';
        const warehouseCode = item.warehouse_id?.warehouse_code || '';
        const batchNumber = item.batch_number || '';
        const referenceId = item.reference_id || '';

        return (
          productName.toLowerCase().includes(searchLower) ||
          productCode.toLowerCase().includes(searchLower) ||
          warehouseName.toLowerCase().includes(searchLower) ||
          warehouseCode.toLowerCase().includes(searchLower) ||
          batchNumber.toLowerCase().includes(searchLower) ||
          referenceId.toLowerCase().includes(searchLower)
        );
      });
    }

    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this opening balance? This will also remove the stock from inventory.")) {
      return;
    }

    try {
      await axios.delete(`${url}/api/openingbalance/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Opening balance deleted successfully");
      fetchOpeningBalances();  // This will refresh the list and automatically recompute totals
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete opening balance");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      product_id: "",
      warehouse_id: "",
      search: "",
      status: "active"
    });
    setShowFilters(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchOpeningBalances();
      toast.success("Data refreshed successfully");
    } catch (error) {
      // silent
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const handleExport = () => {
    if (filteredData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const headers = ["Reference ID", "Product Code", "Product Name", "Warehouse Code", "Warehouse Name", "Quantity", "Unit Cost", "Total Cost", "Batch Number", "Expiry Date", "Transaction Date", "Status", "Created By"];
      const csvData = filteredData.map(item => {
        const productCode = item.product_id?.product_code || item.product_id?.product_no || '';
        const productName = item.product_id?.product_name || '';
        const warehouseCode = item.warehouse_id?.warehouse_code || '';
        const warehouseName = item.warehouse_id?.warehouse_name || '';
        const createdBy = item.created_by?.name || item.created_by?.Usercomp_sure_name || 'System';

        return [
          item.reference_id || '',
          productCode,
          productName,
          warehouseCode,
          warehouseName,
          item.quantity || 0,
          item.unit_cost || 0,
          item.total_cost || 0,
          item.batch_number || '',
          item.expiry_date ? formatDate(item.expiry_date) : '',
          item.transaction_date ? formatDate(item.transaction_date) : '',
          item.status || 'active',
          createdBy
        ];
      });

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `opening-balances-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Export started successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const getProductInfo = (item) => {
    if (typeof item.product_id === 'object' && item.product_id !== null) {
      return {
        name: item.product_id.product_name || 'Unknown Product',
        code: item.product_id.product_code || item.product_id.product_no || 'N/A'
      };
    }
    return { name: 'Unknown Product', code: 'N/A' };
  };

  const getWarehouseInfo = (item) => {
    if (typeof item.warehouse_id === 'object' && item.warehouse_id !== null) {
      return {
        name: item.warehouse_id.warehouse_name || 'Unknown Warehouse',
        code: item.warehouse_id.warehouse_code || 'N/A'
      };
    }
    return { name: 'Unknown Warehouse', code: 'N/A' };
  };

  const getCreatedByInfo = (item) => {
    if (typeof item.created_by === 'object' && item.created_by !== null) {
      return item.created_by.name || item.created_by.Usercomp_sure_name || 'Unknown User';
    }
    return item.created_by || 'System';
  };

  const handleViewDetails = (item) => {
    const details = `
Opening Balance Details:

Reference ID: ${item.reference_id || 'N/A'}
Product: ${getProductInfo(item).name} (${getProductInfo(item).code})
Warehouse: ${getWarehouseInfo(item).name} (${getWarehouseInfo(item).code})
Quantity: ${item.quantity} units
Unit Cost: ${formatCurrency(item.unit_cost)}
Total Cost: ${formatCurrency(item.total_cost)}
Batch Number: ${item.batch_number || 'N/A'}
Expiry Date: ${item.expiry_date ? formatDate(item.expiry_date) : 'N/A'}
Transaction Date: ${formatDate(item.transaction_date)}
Status: ${item.status || 'active'}
Created By: ${getCreatedByInfo(item)}
Created Date: ${formatDate(item.created_at)}
${item.notes ? `Notes: ${item.notes}` : ''}
    `;
    alert(details);
  };

  const handleVerify = async (id) => {
    if (!window.confirm("Are you sure you want to verify this opening balance?")) {
      return;
    }

    try {
      await axios.post(`${url}/api/openingbalance/${id}/verify`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Opening balance verified successfully");
      fetchOpeningBalances();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to verify opening balance");
    }
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  if (loading && openingBalances.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading">Loading opening balances...</div>
      </div>
    );
  }

  return (
    <div className="district-container">
      <div className="header">
        <div className="white-text">
          <h2>Opening Balance List</h2>
          {userCompany && (
            <p className="company-info">
              <Building size={14} />
              {typeof userCompany === 'object'
                ? `${userCompany.company_name || 'Unknown Company'} (${userCompany.company_code || 'N/A'})`
                : userCompany}
            </p>
          )}
        </div>
        <div className="header-actions">
          <button
            onClick={() => navigate("/AddOpeningBalance?tab=bulk")}
            className="upload-button"
          >
            <Upload size={16} /> Bulk Upload
          </button>
          <button onClick={() => navigate("/AddOpeningBalance")} className="add-button">
            <Plus size={16} /> Add Opening Balance
          </button>
          <button onClick={handleRefresh} className="refresh-button" disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? "spinning" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Stats Bar - Now using computed totals from actual data */}
      <div className="stats-bar">
        <div className="stat-item">
          <Package size={16} />

          <div className="white-text">
            <span className="stat-value">{totalRecords}</span>
            <span className="stat-label">Total Records</span>
          </div>
        </div>
        <div className="stat-item">
          <Hash size={16} />
          <div className="white-text">
            <span className="stat-value">{totalQuantity}</span>
            <span className="stat-label">Total Quantity</span>
          </div>
        </div>
        <div className="stat-item">
          <DollarSign size={16} />
           <div className="white-text">
            <span className="stat-value">{formatCurrency(totalValue)}</span>
            <span className="stat-label">Total Value</span>
          </div>
        </div>
        <div className="stat-item">
           <div className="white-text">
            <span className="stat-value">{filteredData.length}</span>
            <span className="stat-label">Showing</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`filters-container ${showFilters ? 'expanded' : ''}`}>
        <div className="filter-header" onClick={() => setShowFilters(!showFilters)}>
          <Filter size={16} />
          <h4>Filters</h4>
          <span className="filter-toggle">
            {showFilters ? '▼' : '►'}
          </span>
        </div>

        {showFilters && (
          <div className="filter-content">
            <div className="filter-fields">
              <div className="filter-field">
                <Search size={16} />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search by product, warehouse, batch, or reference..."
                  className="search-input"
                />
              </div>

              <select
                name="product_id"
                value={filters.product_id}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Products</option>
                {products.map(product => (
                  <option key={product._id} value={product._id}>
                    {product.product_code || product.product_no} - {product.product_name}
                  </option>
                ))}
              </select>

              <select
                name="warehouse_id"
                value={filters.warehouse_id}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Warehouses</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse._id} value={warehouse._id}>
                    {warehouse.warehouse_code} - {warehouse.warehouse_name}
                  </option>
                ))}
              </select>

              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
                <option value="">All Status</option>
              </select>

              <button onClick={clearFilters} className="clear-filters-btn">
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Actions Bar */}
      <div className="actions-bar">
        <div className="results-count">
          Showing {paginatedData.length} of {filteredData.length} records
        </div>
        <div className="action-buttons">
          <button onClick={handleExport} className="export-button">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      {/* Opening Balances Table */}
      <div className="table-container">
        <table className="district-table">
          <thead>
            <tr>
              <th>Reference ID</th>
              <th>Product</th>
              <th>Warehouse</th>
              <th>Quantity</th>
              <th>Unit Cost</th>
              <th>Total Cost</th>
              <th>Batch No.</th>
              <th>Transaction Date</th>
              <th>Status</th>
              <th>Created By</th>
              {/* <th style={{ textAlign: "center" }}>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  {openingBalances.length === 0
                    ? "No opening balances found. Create your first opening balance!"
                    : "No matching records found. Try changing your filters."
                  }
                  {openingBalances.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={clearFilters}
                        className="clear-filters-btn"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                  {openingBalances.length === 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <button
                        onClick={() => navigate("/AddOpeningBalance")}
                        className="add-first-button"
                      >
                        <Plus size={14} /> Add First Opening Balance
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => {
                const productInfo = getProductInfo(item);
                const warehouseInfo = getWarehouseInfo(item);
                const createdBy = getCreatedByInfo(item);

                return (
                  <tr key={item._id} className={item.status === 'inactive' ? 'inactive-row' : ''}>
                    <td>
                      <div className="reference-id">
                        <div className="ref-id-badge">
                          <Hash size={12} />
                          <strong>{item.reference_id || 'N/A'}</strong>
                        </div>
                        {item.opening_balance_id && (
                          <small className="ref-id-sub">ID: {item.opening_balance_id}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="product-info">
                        <Package size={14} />
                        <div>
                          <strong>{productInfo.name}</strong>
                          <small>{productInfo.code}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="warehouse-info">
                        <Warehouse size={14} />
                        <div>
                          <strong>{warehouseInfo.name}</strong>
                          <small>{warehouseInfo.code}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="quantity-cell">
                        <span className="quantity-value">
                          {(item.quantity || 0).toLocaleString()}
                        </span>
                        <small>units</small>
                      </div>
                    </td>
                    <td>
                      {formatCurrency(item.unit_cost)}
                    </td>
                    <td>
                      <div className="total-cost-cell">
                        <strong>{formatCurrency(item.total_cost)}</strong>
                      </div>
                    </td>
                    <td>
                      {item.batch_number ? (
                        <span className="batch-badge">{item.batch_number}</span>
                      ) : (
                        <span className="empty-field">-</span>
                      )}
                    </td>
                    <td>
                      <div className="date-cell">
                        <Calendar size={12} />
                        {formatDate(item.transaction_date)}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${item.status || 'active'}`}>
                        {item.status === 'active' ? 'Active' : 'Inactive'}
                        {item.is_verified && <CheckCircle size={10} />}
                      </span>
                    </td>
                    <td>
                      <div className="user-info">
                        <User size={12} />
                        <span>{createdBy}</span>
                      </div>
                    </td>
                    {/* <td className="actions">
                      <button
                        onClick={() => handleViewDetails(item)}
                        className="view-btn"
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      {item.status === 'active' && !item.is_verified && (
                        <button
                          onClick={() => handleVerify(item._id)}
                          className="verify-btn"
                          title="Verify"
                        >
                          <CheckCircle size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/openingbalance/edit/${item._id}`)}
                        className="edit-btn"
                        title="Edit"
                        disabled={item.status === 'inactive'}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="delete-btn"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td> */}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page =>
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              )
              .map((page, index, array) => {
                const showEllipsis = index > 0 && page - array[index - 1] > 1;
                return (
                  <React.Fragment key={page}>
                    {showEllipsis && <span className="ellipsis">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default OpeningBalanceList;